import React, { ReactNode, useEffect } from "react";
import { Modal as AntModal } from "antd";
import CustomButton from "@Components/Button/Button";
import { useForm } from "react-hook-form";

type FormValues = {
  reason: string;
};

export interface ActionButton {
  label: string;
  onClick: (reason?: string) => void;
  variant?: "cancel" | "confirm" | "primary" | "danger" | "secondary";
  className?: string;
  isLoading?: boolean;
  disabled?: boolean;
}

interface ConfirmationModalProps {
  icon?: ReactNode;
  title: string;
  content: string | ReactNode;
  actionButtons?: ActionButton[];
  onCancel?: () => void;
  onConfirm?: (reason?: string) => void;
  confirmLabel?: string;
  cancelLabel?: string;
  open?: boolean;
  closable?: boolean;
  centered?: boolean;
  width?: number | string;
  subContent?: string | ReactNode;
  afterSubContent?: string | ReactNode;
  showReasonInput?: boolean;
  reasonPlaceholder?: string;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  icon,
  title,
  content,
  actionButtons,
  onCancel,
  onConfirm,
  confirmLabel = "Yes",
  cancelLabel = "Cancel",
  open = true,
  closable = false,
  centered = true,
  width,
  subContent,
  afterSubContent,
  showReasonInput = false,
  reasonPlaceholder = "Enter reason...",
}) => {
  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      reason: "",
    },
  });

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);
  const defaultIcon = (
    <div className="relative w-16 h-16 mx-auto mb-6">
      <div className="absolute inset-0 rounded-full bg-[#F0624D] opacity-20 animate-pulse"></div>
      <div className="absolute inset-0 rounded-full bg-[#F0624D] opacity-30 scale-75"></div>

      <div className="relative w-full h-full rounded-full bg-gradient-to-br from-[#E7503D] to-[#F0624D] flex items-center justify-center shadow-lg">
        <span className="text-white text-2xl font-bold">?</span>
      </div>
    </div>
  );

  const defaultButtons: ActionButton[] = [
    {
      label: cancelLabel,
      onClick: onCancel || (() => {}),
      variant: "cancel",
    },
    {
      label: confirmLabel,
      onClick: (reason?: string) => {
        if (onConfirm) {
          onConfirm(reason);
        }
      },
      variant: "confirm",
    },
  ];

  const buttons = actionButtons || defaultButtons;

  const getButtonClassName = (variant?: string) => {
    switch (variant) {
      case "cancel":
        return "!bg-white !text-secondaryGray border border-strokeBorder !capitalize !font-semibold";
      case "confirm":
        return "!bg-[#1F3D2B] !text-white hover:!bg-[#1F3D2B]  !border-none";
      case "danger":
        return "!bg-[#D00606] !text-white hover:!bg-[#D00606] !border-none !capitalize  !font-semibold";
      case "secondary":
        return "!bg-[#F9FAFB] !text-[#1F2937] hover:!bg-gray-100 !border-none !capitalize !font-semibold";
      default:
        return "!bg-[#111827] !text-white hover:!bg-[#1F2937] !border-none !capitalize !font-semibold";
    }
  };

  const handleCancel = () => {
    reset();
    if (onCancel) {
      onCancel();
    }
  };

  const handleButtonClick = (button: ActionButton) => {
    // Skip validation for cancel/secondary buttons
    if (button.variant === "cancel" || button.variant === "secondary") {
      button.onClick();
      return;
    }

    if (showReasonInput) {
      handleSubmit((data) => {
        button.onClick(data.reason);
      })();
    } else {
      button.onClick();
    }
  };

  const modalContent = (
    <div className="flex flex-col items-center !justify-center px-6 py-4 !rounded-2xl gap-2">
      <div className=" !text-center">{icon || defaultIcon}</div>
      <div className="flex flex-col gap-2 text-center mt-4 mb-2 w-full">
        <h2 className="!text-xl !font-semibold text-textTitle !mt-0 ">
          {title}
        </h2>

        <div className={`!text-base text-secondaryGray pt-2 text-center leading-3`}>
          {typeof content === "string" ? (
            <p className="!font-Nunito">{content}</p>
          ) : (
            content
          )}
        </div>

        {subContent && (
          <div className="!text-base pt-4 !leading-5 mt-0 text-secondaryGray text-center ">
            {subContent}
          </div>
        )}

        {afterSubContent && (
          <div className="text-md pt-4 !leading-3 mt-0 text-secondaryGray text-center ">
            {afterSubContent}
          </div>
        )}

        {showReasonInput && (
          <div className="w-full mt-4 px-0">
            <div className="w-full border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-[#F0624D] focus-within:border-transparent overflow-hidden">
              <textarea
                {...register("reason", {
                  required: showReasonInput ? "Reason is required" : false,
                  minLength: {
                    value: 10,
                    message: "Reason must be at least 10 characters",
                  },
                  maxLength: {
                    value: 500,
                    message: "Reason must not exceed 500 characters",
                  },
                })}
                placeholder={reasonPlaceholder}
                className="w-full min-h-[100px] p-3 border-none focus:outline-none resize-none text-sm"
                style={{ overflowY: "auto" }}
                rows={4}
                maxLength={500}
              />
            </div>
            {errors.reason && (
              <p className="text-red-500 text-xs mt-1 text-left">
                {errors.reason.message}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-4 w-[60%] px-0 pb-4 mt-6">
        {buttons.map((button, index) => (
          <CustomButton
            key={index}
            title={button.label}
            onClick={() => handleButtonClick(button)}
            className={`flex-1 !min-w-0 ${getButtonClassName(button.variant)} ${
              button.className || ""
            }`}
            htmlType="button"
            isLoading={button?.isLoading}
            disabled={button?.disabled}
          />
        ))}
      </div>
    </div>
  );

  return (
    <AntModal
      open={open}
      onCancel={handleCancel}
      footer={null}
      closable={closable}
      centered={centered}
      width={width}
      className="!rounded-xl"
    >
      {modalContent}
    </AntModal>
  );
};

export default ConfirmationModal;
